"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowRight, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqs: FaqItem[] = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Woking?",
  answer: "Yes. BMF Scaffolding regularly covers Woking and surrounding areas across Surrey, including Brookwood, Horsell, Mayford, Knaphill, and West Byfleet. We supply, erect, inspect, and dismantle scaffolding for residential, commercial, and industrial sites."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Woking?",
  answer: "We provide independent access scaffolding, temporary roof systems, edge protection, chimney and gable access, new build site frameworks, refurbishment scaffolding, and bespoke access solutions designed to NASC safety standards."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We frequently supply access for pitched and flat roofing replacements, re-pointing, chimney rebuilds, solar panel installations, and complete temporary roof weather protection structures."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We partner directly with main contractors, residential developers, local builders, roofing contractors, facilities managers, and local authorities, managing on-site safety documentation and scheduled handovers."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Restricted alleys, narrow passageways, uneven ground, and tight urban boundaries can be managed using tailored design schemes, bridging beams, cantilever towers, and specialist access configurations after a site survey."
}, {
  id: "item-6",
  question: "How do I request a quote in Woking?",
  answer: "You can submit project details through our online quote request form or contact our team directly. Providing site drawings, project scope, photos, or scheduling a site survey allows us to issue an itemised proposal based on your exact access requirements."
}];
export default function WokingFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="wokingfaq" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column: Context & Summary */}
          <motion.div className="lg:col-span-5 space-y-6" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} variants={containerVariants}>
            <motion.div variants={itemVariants} className="space-y-3">
              <Badge variant="outline" className="gap-1.5 px-3 py-1 font-medium">
                <HelpCircle className="size-3.5 text-primary" />
                Woking & Surrey Area
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
                Frequently asked questions about scaffolding in Woking
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Clear answers to common questions regarding scaffolding hire,
                site access, and planning for projects across Woking and Surrey.
              </p>
            </motion.div>

            {/* Accreditation Callout Box */}
            <motion.div variants={itemVariants} className="rounded-lg border border-border bg-card p-5 sm:p-6 shadow-xs space-y-3">
              <div className="flex items-center gap-2.5 text-foreground font-semibold text-sm">
                <ShieldCheck className="size-5 text-primary shrink-0" />
                <span>Certified safety & industry standards</span>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                BMF Scaffolding operates under verified accreditations including
                NASC and CHAS, upholding strict regulatory compliance on every
                structure.
              </p>
              <div className="pt-2">
                <Button asChild variant="outline" size="sm" className="w-full sm:w-auto text-xs">
                  <Link to="/HealthSafetyAccreditationsPage">
                    View safety accreditations
                    <ArrowRight className="size-3.5 ml-1" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Contact / Action prompt */}
            <motion.div variants={itemVariants} className="rounded-lg bg-muted p-5 sm:p-6 border border-border space-y-4">
              <div className="space-y-1">
                <h3 className="font-semibold text-foreground text-base">
                  Need a quote or site survey?
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Send your drawings, specifications, or site photos for an
                  itemised scaffolding proposal.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button asChild className="w-full sm:w-auto">
                  <Link to="/ContactRequestQuotePage">
                    Request a quote
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/AreasWeCoverPage">
                    Other areas covered
                  </Link>
                </Button>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column: Accordion Items */}
          <motion.div className="lg:col-span-7" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} variants={containerVariants}>
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-3">
              {faqs.map((faq, i) => <motion.div key={faq.id} data-index={i} variants={itemVariants} className="rounded-lg border border-border bg-card shadow-2xs overflow-hidden">
                  <AccordionItem value={faq.id} className="border-b-0 px-4 sm:px-6">
                    <AccordionTrigger className="text-left font-semibold text-base sm:text-lg text-foreground py-4 sm:py-5 hover:no-underline">
                      <span className="pr-4">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-5 pt-0">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>;
}